import { loadScript } from "@faire/deprecated-retailer-visitor-shared/lib/loadScript";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import {
  getWindow,
  getWindowOrThrow,
} from "@faire/web/common/globals/getWindow";
import { logError } from "@faire/web/common/logging";
import debug from "debug";

/**
 * Google Tag Manager (or GTM) is a script that we include in our page. Data
 * sent to GTM cant be forwarded on to other tracking scripts and pixels. For
 * us, this is mostly Google Analytics, but the data is also used for Google
 * ad tracking and Bing ad tracking, among other things.
 *
 * The script works by monkeypatching an array-like object on the window object
 * to capture called the dataLayer. Objects pushed to this array will be
 * processed by the GTM script.
 *
 * The dataLayer has the concept of variables and events.
 *
 * For simple variables, these are just the keys in the object that you push
 * to the dataLayer. Pushing a new value for the same key will update the value
 * of that variable.
 *
 * Events look just like pushing a set of variables, but includes a special
 * key called "event" with the name of the event being fired. Events are the
 * things that actually get sent up to GTM. When an event is fired, the latest
 * value of all the variables is also sent to GTM.
 */

const log = debug("IF:GTM");

/**
 * Encapsulates operations on the underlying dataLayer object
 */
class GTMDataLayer {
  private push = (obj: unknown) => {
    getWindow()?.dataLayer?.push(obj);
  };

  pushVariables = (vars: { [key: string]: unknown }) => {
    log(`var: ${JSON.stringify(vars)}`);

    this.push(vars);
  };

  pushEvent = (event: string, vars?: { [key: string]: unknown }) => {
    log(`event: ${event} ${vars ? JSON.stringify(vars) : ""}`);

    this.push({ event, ...vars });
  };
}

export class GoogleTagManager {
  private dl: GTMDataLayer;

  constructor(gtmTagId: string) {
    const window = getWindowOrThrow();
    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    if (!getDocumentOrThrow().getElementById("_gtm")) {
      log(`loading GTM tag`);
      this.loadGtmTag(gtmTagId);
    } else {
      log(`existing GTM tag found`);
    }

    this.dl = new GTMDataLayer();
  }

  private loadGtmTag = (gtmTagId: string) => {
    // Do the same thing as the stock GTM snippet
    getWindow()?.dataLayer?.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    loadScript(
      `https://www.googletagmanager.com/gtm.js?id=${gtmTagId}`,
      "_gtm"
    ).catch((e) => logError(e));
  };

  pushVariables = (vars: { [key: string]: unknown }) => {
    this.dl.pushVariables(vars);
  };

  pushEvent = (event: string, vars?: { [key: string]: unknown }) => {
    this.dl.pushEvent(event, vars);
  };
}
